import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, GraduationCap, CheckCircle2, Briefcase, Euro, Accessibility, Target, BookOpen, Newspaper, ExternalLink, Award, ShieldCheck, Download } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/Accordion';
import { FORMATIONS, LINKS } from '../data/constants';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
export function FormationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const formation = FORMATIONS.find(f => f.id === id);
  if (!formation) {
    return <Navigate to="/formations" replace data-id="element-1280" data-mp="1o9vpqi0m7oly" />;
  }
  return <div className="flex flex-col w-full pb-24 bg-muted" data-id="element-1281" data-mp="1tat9v056qsq3">
      {/* Custom Header for Formation */}
      <div className="relative bg-lavande py-16 md:py-24 overflow-hidden" data-id="element-1282" data-mp="1fbabgz26evqz">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1283" data-mp="1jc41bxz6ikmi" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1284" data-mp="272gcritmuzhu" />

        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1285" data-mp="1y8l6g7grz30m">
          <div className="flex flex-wrap gap-3 mb-8" data-id="element-1286" data-mp="22agcfp9ni4kq">
            <Badge className="bg-primary text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1287" data-mp="228vctl8hbvg0">
              {formation.level}
            </Badge>
            <Badge variant="outline" className="bg-white/50 border-primary/20 text-primary-dark text-sm px-4 py-1.5 font-semibold" data-id="element-1288" data-mp="2uritmogg0oba">
              {formation.rncp}
            </Badge>
            <Badge variant="secondary" className="bg-brand-orange text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1289" data-mp="30mz260qyyot8">
              Session {formation.session}
            </Badge>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-primary-dark mb-6 tracking-tight leading-[1.1]" data-id="element-1290" data-mp="2lodh2ay79gyh">
            {formation.title}
          </h1>

          <p className="text-xl md:text-2xl text-primary-dark/80 max-w-3xl leading-relaxed mb-10 font-medium" data-id="element-1291" data-mp="2gwdo52gjrobe">
            {formation.description}
          </p>

          <div className="flex flex-wrap gap-8 text-base font-medium text-primary-dark bg-white p-8 rounded-2xl shadow-xl inline-flex border-none" data-id="element-1292" data-mp="27xryyogklb1n">
            <div className="flex items-center gap-4 group" data-id="element-1293" data-mp="230087xrnvuz1">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1294" data-mp="1y040bnfufje7">
                <Clock className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1295" data-mp="1nx8fslwi5p9b" />
              </div>
              <div data-id="element-1296" data-mp="1svs60yi4pn7h">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1297" data-mp="1xtbj3v33btxu">
                  Durée
                </span>
                <span className="font-bold" data-id="element-1298" data-mp="22r3jzelpqqe6">{formation.duration}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1299" data-mp="155fqp2kk0o6v"></div>
            <div className="flex items-center gap-4 group" data-id="element-1300" data-mp="1a37nob0230hx">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1301" data-mp="1a1d3iywlzcew">
                <Calendar className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1302" data-mp="1jtsm9yfqc50u" />
              </div>
              <div data-id="element-1303" data-mp="meenu3gbc8i1">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1304" data-mp="md3msbe95eum">
                  Rythme
                </span>
                <span className="font-bold" data-id="element-1305" data-mp="rb57t1v8no8q">{formation.rhythm}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1306" data-mp="2t02o5uxzfav5"></div>
            <div className="flex items-center gap-4 group" data-id="element-1307" data-mp="2o4djjd73v3rz">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1308" data-mp="32row9if0wtto">
                <GraduationCap className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1309" data-mp="2y0gn8nhlvah1" />
              </div>
              <div data-id="element-1310" data-mp="3br26idaz1mfg">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1311" data-mp="3bupxp5sy1wr0">
                  Rentrée
                </span>
                <span className="font-bold" data-id="element-1312" data-mp="3lqkmyjroaq2r">{formation.rentree}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-6xl mx-auto mt-16" data-id="element-1313" data-mp="3gs9f2ekxupn6">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1314" data-mp="1rc9y4bb2vvwz">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-1315" data-mp="1mdp0mjn7nqc8">
            <Tabs defaultValue="programme" className="w-full" data-id="element-1316" data-mp="1mf9s20e8at5v">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 h-auto p-1" data-id="element-1317" data-mp="1v4qp18ujp3s">
                <TabsTrigger value="programme" className="py-3 text-sm md:text-base" data-id="element-1318" data-mp="6vakoo2l9np1">
                  Programme
                </TabsTrigger>
                <TabsTrigger value="modalites" className="py-3 text-sm md:text-base" data-id="element-1319" data-mp="3t9txwhftuwtx">
                  Modalités
                </TabsTrigger>
                <TabsTrigger value="debouches" className="py-3 text-sm md:text-base" data-id="element-1320" data-mp="3k9dtn5r9flo0">
                  Débouchés
                </TabsTrigger>
                <TabsTrigger value="qualite" className="py-3 text-sm md:text-base" data-id="element-1321" data-mp="3ab7totp0kpls">
                  Qualité
                </TabsTrigger>
              </TabsList>

              {/* TAB: Programme */}
              <TabsContent value="programme" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1322" data-mp="18jbl9sy57vv8">
                <section data-id="element-1323" data-mp="123begmig10gb">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1324" data-mp="12332cwu59z66">
                    <Target className="w-6 h-6 text-primary" data-id="element-1325" data-mp="x5jct0rzxzyx" />
                    Objectifs pédagogiques mesurables
                  </h2>
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-100" data-id="element-1326" data-mp="t5iyrh8t7qse">
                    <p className="text-muted-foreground leading-relaxed" data-id="element-1327" data-mp="1pvjkf7dvtr5h">
                      {formation.objectives}
                    </p>
                  </div>
                </section>

                <section data-id="element-1328" data-mp="1g1t08eosg2vl">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1329" data-mp="1c20ndpsd3akf">
                    <BookOpen className="w-6 h-6 text-primary" data-id="element-1330" data-mp="1c07nctc97ole" />
                    Blocs de compétences
                  </h2>
                  <Accordion type="single" collapsible className="w-full" data-id="element-1331" data-mp="23sopp55rulzo">
                    {formation.certification.blocs.map((bloc, i) => <AccordionItem key={i} value={`bloc-${i}`} data-id="element-1332" data-mp="3mhstlg1uxawm" data-mpi={i}>
                        <AccordionTrigger className="text-left font-semibold hover:text-primary" data-id="element-1333" data-mp="3rg2orqnl0bi8">
                          {bloc.code}
                        </AccordionTrigger>
                        <AccordionContent data-id="element-1334" data-mp="4av0g85boqnw">
                          <p className="text-muted-foreground mt-2" data-id="element-1335" data-mp="4bmw1iyx6oe6">
                            {bloc.description}
                          </p>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </section>
              </TabsContent>

              {/* TAB: Modalités */}
              <TabsContent value="modalites" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1336" data-mp="n12ywcy2usxr">
                <section className="space-y-6" data-id="element-1337" data-mp="ry346ln3rrnt">
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1338" data-mp="x0293zyhqwmv">
                    <div data-id="element-1339" data-mp="7vv7lvelk9vn">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1340" data-mp="7v2brph6f4sa">Public visé</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1341" data-mp="2x0sp4jr35gt">
                        {formation.public}
                      </p>
                    </div>
                    <div data-id="element-1342" data-mp="cuexu80847tt">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1343" data-mp="cw9irmbhvzxw">Prérequis</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1344" data-mp="3la8dylqn7hac">
                        {formation.prerequisites}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1345" data-mp="3u4cyyoj3hyhy">
                    <h3 className="text-lg font-bold mb-2" data-id="element-1346" data-mp="3u4wkef7yf50q">
                      Méthodes mobilisées
                    </h3>
                    <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1347" data-mp="31m90320s7u0p">
                      {formation.methods}
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1348" data-mp="2ngp8nycaf31h">
                    <div data-id="element-1349" data-mp="2nf5rm5it4nyn">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1350" data-mp="2s7p5l3lepl3z">
                        Évaluations en formation
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1351" data-mp="2dkdrov1r5p0p">
                        {formation.evaluation}
                      </p>
                    </div>
                    <div data-id="element-1352" data-mp="368i0hpfmei7m">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1353" data-mp="365vqlj59bqm8">
                        Épreuve certificative
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1354" data-mp="2waby76pwja28">
                        {formation.epreuve}
                      </p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1355" data-mp="1kt2h2qu8wu43">
                    <div data-id="element-1356" data-mp="1ptqh220gclag">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1357" data-mp="1pv1l82xt7v23">Accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1358" data-mp="2x59fxvgi8kvj">
                        {formation.acces}
                      </p>
                    </div>
                    <div data-id="element-1359" data-mp="2neogk5t0bcbk">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1360" data-mp="2nfg6azzxaftx">Délai d'accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1361" data-mp="2sdhwe4vrnfew">
                        {formation.delaiAcces}
                      </p>
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* TAB: Débouchés */}
              <TabsContent value="debouches" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1362" data-mp="24iyigto7302y">
                <section data-id="element-1363" data-mp="2p8u1n0tm9rq">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1364" data-mp="2q0hgak9l16x">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1365" data-mp="7o21axpil4jg" />
                    Débouchés officiels
                  </h2>
                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-1366" data-mp="1s49ta2y4eveu">
                    {formation.opportunities.map((job, i) => <Badge key={i} variant="secondary" className="text-sm px-4 py-2" data-id="element-1367" data-mp="21wxquzwxd3em" data-mpi={i}>
                        {job}
                      </Badge>)}
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6" data-id="element-1368" data-mp="26vsimrcve2av">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1369" data-mp="2kv9ygh86u583">
                      Codes ROME correspondants :
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1370" data-mp="2fxi99uyxgx7l">
                      {formation.romeCodes.map((code, i) => <Badge key={i} variant="outline" className="bg-white" data-id="element-1371" data-mp="qjd4agid2l9c" data-mpi={i}>
                          {code}
                        </Badge>)}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1372" data-mp="2lmrvv3b8r18p">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1373" data-mp="2qlmhed0lf3wi">
                      Secteurs :
                    </span>
                    <p className="text-muted-foreground" data-id="element-1374" data-mp="2zkpzbdo0xd9f">
                      {formation.secteurs}
                    </p>
                  </div>
                </section>

                <section data-id="element-1375" data-mp="34hgoqrlpxl7i">
                  <h2 className="text-2xl font-bold font-heading mb-4 flex items-center gap-2" data-id="element-1376" data-mp="39hun1hxipgsc">
                    <GraduationCap className="w-6 h-6 text-primary" data-id="element-1377" data-mp="3e8kt6xdrpno3" />
                    Poursuites d'études
                  </h2>
                  <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1378" data-mp="3i9ei9u2y58ik">
                    {formation.furtherStudies}
                  </p>
                </section>
              </TabsContent>

              {/* TAB: Qualité */}
              <TabsContent value="qualite" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1379" data-mp="674xl668i59v">
                <section data-id="element-1380" data-mp="170hvid5hekp">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1381" data-mp="1ao9ud2y5sxb">
                    Indicateurs de résultats
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1382" data-mp="3odt0qqhq4icw">
                    {formation.indicators.map((ind, i) => <Card key={i} className="bg-primary/5 border-none" data-id="element-1383" data-mp="oxedtjyuvxkk" data-mpi={i}>
                        <CardContent className="p-6 text-center flex flex-col justify-center h-full" data-id="element-1384" data-mp="jzunhlovvuc9">
                          <div className="text-3xl font-bold text-primary mb-2 font-heading" data-id="element-1385" data-mp="f71jj018oq18">
                            {ind.label}
                          </div>
                          <div className="text-sm font-medium text-foreground" data-id="element-1386" data-mp="17o5ii87wezsm">
                            {ind.value}
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                </section>

                <section data-id="element-1387" data-mp="seqyqca2zkha">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1388" data-mp="sfa8h5pkv2k8">
                    Repères RNCP
                  </h2>
                  <Card className="border-none bg-slate-50" data-id="element-1389" data-mp="egaqggwkllx1">
                    <CardContent className="p-6 space-y-4" data-id="element-1390" data-mp="ig1ewdymjpye">
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1391" data-mp="iinmgdlim5gm">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1392" data-mp="1688hvvyuuv6s">
                          Intitulé exact
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1393" data-mp="xen34cmoxdxd">
                          {formation.certification.intitule}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1394" data-mp="1p4hu0dq75bo5">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1395" data-mp="1tw9mitswxrgx">
                          Niveau / Domaine
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1396" data-mp="3ekbxnnyzgaq8">
                          {formation.certification.niveauDomaine}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1397" data-mp="3ob6nt3fcnugy">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1398" data-mp="3jddgg72b8uf3">
                          Certificateur
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1399" data-mp="2qopro1icrmws">
                          {formation.certification.certificateur}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1400" data-mp="30mndyjgnj9mr">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1401" data-mp="kuzjqvt0vv7n">
                          Enregistrement
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1402" data-mp="fvmwi9y8om4j">
                          {formation.certification.enregistrement}
                        </div>
                      </div>
                      {formation.certification.derniereDelivrance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1403" data-mp="wx98d8bwenpk">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1404" data-mp="11wvobpj42i22">
                            Dernière délivrance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1405" data-mp="470qlhl9y5bi">
                            {formation.certification.derniereDelivrance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1406" data-mp="iy9mu9jomoxm">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1407" data-mp="3ho9rdm9ylj9w">
                          Capitalisation
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1408" data-mp="3mkqdyc1ifpkv">
                          {formation.certification.capitalisation}
                        </div>
                      </div>
                      {formation.certification.correspondance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1409" data-mp="1upz5qrfk66ep">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1410" data-mp="1pqvzj3nudu55">
                            Correspondance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1411" data-mp="1g25oz3eqkxuj">
                            {formation.certification.correspondance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4" data-id="element-1412" data-mp="171zdto6ig9yj">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1413" data-mp="16zmp8s68z1ci">
                          Lien officiel
                        </div>
                        <div className="sm:col-span-2 font-medium break-all" data-id="element-1414" data-mp="x4bawayr3w5a">
                          <a href={`https://${formation.certification.lien}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-id="element-1415" data-mp="2lwxmbx7y5qeo">
                            {formation.certification.lien}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </TabsContent>
            </Tabs>

            {/* Points forts de la formation */}
            <Card className="border-none shadow-lg bg-white overflow-hidden" data-id="element-1416" data-mp="1ta36jdm2rf7j">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1417" data-mp="1y73eju2his1a" />
              <CardHeader className="pt-8" data-id="element-1418" data-mp="1y5iig00iv11b">
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark flex items-center gap-2" data-id="element-1419" data-mp="1jacedikzlcam">
                  <Target className="w-6 h-6 text-brand-orange" data-id="element-1420" data-mp="1o974l5laa34r" />
                  Les métiers visés
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 pt-2" data-id="element-1421" data-mp="10mldat9ciaz3">
                <p className="text-muted-foreground leading-relaxed mb-6" data-id="element-1422" data-mp="10nwh2t2sjiq7">
                  À l'issue de cette formation, vous pourrez exercer sur de
                  nombreux postes recherchés par les entreprises :
                </p>
                <div className="grid sm:grid-cols-2 gap-3" data-id="element-1423" data-mp="1nmrkefj2iar5">
                  {formation.opportunities.map((job, i) => <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-1424" data-mp="1jlzarl6aa3uc" data-mpi={i}>
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0" data-id="element-1425" data-mp="1sdrdtaszaaow" />
                      <span className="font-medium text-primary-dark text-sm" data-id="element-1426" data-mp="27aryiucr55dv">
                        {job}
                      </span>
                    </div>)}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-1427" data-mp="2euf468v5po28">
                  <Button className="w-full group bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-md" asChild data-id="element-1428" data-mp="2eskmd3f6mc2d">
                    <Link to="/candidater" data-id="element-1429" data-mp="2nuk3rwu3zi5u">
                      Je postule à cette formation
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1430" data-mp="2ju03wli5i0g0" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* CAP 360 — Accompagnement recherche d'alternance */}
            <Cap360 data-id="element-1431" data-mp="24xsmfsy4nd4f" />
          </div>

          {/* Sidebar / Sticky Actions */}
          <div className="lg:col-span-1" data-id="element-1432" data-mp="204gaaiamwysk">
            <div className="sticky top-28 space-y-8" data-id="element-1433" data-mp="203enjgk9anv0">
              <Card className="border-none shadow-xl bg-white overflow-hidden" data-id="element-1434" data-mp="1cevf9117ib2x">
                <div className="h-2 w-full bg-gradient-to-r from-primary to-brand-orange" data-id="element-1435" data-mp="18ev2rlxo49yi" />
                <CardHeader className="bg-white pb-6 pt-8" data-id="element-1436" data-mp="yaocwg58h00e">
                  <CardTitle className="text-2xl font-extrabold font-heading text-center text-primary-dark" data-id="element-1437" data-mp="y93fmirjb2yu">
                    Intéressé(e) par cette formation ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-0 space-y-4" data-id="element-1438" data-mp="tbbgp4sh2gl5">
                  <Button className="w-full text-lg py-7 group bg-brand-orange hover:bg-[#E67E22] text-white shadow-md" asChild data-id="element-1439" data-mp="tbtj6j5q9r1i">
                    <Link to="/candidater" data-id="element-1440" data-mp="1lyo57ki70yh3">
                      Je candidate maintenant
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1441" data-mp="171oq7bl0i3cl" />
                    </Link>
                  </Button>
                  {formation.catalogueUrl && <Button variant="outline" className="w-full py-6 bg-lavande border-2 border-primary/30 text-primary-dark hover:bg-lavande/70 font-bold text-base" asChild data-id="element-1442" data-mp="3rs5zux7hniui">
                      <a href={formation.catalogueUrl} download data-id="element-1443" data-mp="1y9otus4enggz">
                        <Download className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1444" data-mp="1ycb9lf1g92aa" />
                        Télécharger le programme (PDF)
                      </a>
                    </Button>}
                  <Button variant="outline" className="w-full py-6 border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-1445" data-mp="1oikpemccve0e">
                    <Link to="/qualite#indicateurs" data-id="element-1446" data-mp="1ak48k2ks474j">
                      <Target className="w-5 h-5 mr-2" data-id="element-1447" data-mp="1akw5phu0ycwx" />
                      Voir nos indicateurs de résultats
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white" data-id="element-1448" data-mp="34way66ef6qgd">
                <CardContent className="p-8 space-y-8" data-id="element-1449" data-mp="300vhz7sxwigs">
                  <div className="group" data-id="element-1450" data-mp="37jcvrq7umjfk">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1451" data-mp="37ik0pmi95id9">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1452" data-mp="3cgd5cb3wq4bf">
                        <Euro className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1453" data-mp="3m8iuze6os9ng" />
                      </div>
                      Tarif et financement
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13" data-id="element-1454" data-mp="2tjc0cqacs47h">
                      {formation.price}
                    </p>
                  </div>

                  <div className="pt-8 border-t border-slate-100 group" data-id="element-1455" data-mp="2yh3pxdnize8i">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1456" data-mp="33i9j1cl3l3ik">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1457" data-mp="dro2msnim7nm">
                        <Accessibility className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1458" data-mp="gccb0k8y3in6" />
                      </div>
                      Accessibilité handicap
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13 mb-4" data-id="element-1459" data-mp="qas42srj91zb">
                      {formation.handicap}
                    </p>
                    <div className="pl-13" data-id="element-1460" data-mp="lax8wd0q5q8g">
                      <Button variant="outline" size="sm" className="border-primary/20 text-primary-dark hover:bg-primary/5" asChild data-id="element-1461" data-mp="zbgg5hxd8s9k">
                        <Link to="/accessibilite" data-id="element-1462" data-mp="190ro318inqvv">
                          En savoir plus
                          <ArrowRight className="w-4 h-4 ml-2" data-id="element-1463" data-mp="143hoisrexyub" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Réussite & Centre d'examen — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-muted" data-id="element-1464" data-mp="21mgbg8igiexd">
          <div className="container max-w-5xl mx-auto space-y-10" data-id="element-1465" data-mp="1nnyqujnulijo">
            {/* 100% de réussite */}
            <div className="bg-primary-dark text-white rounded-3xl shadow-xl overflow-hidden relative" data-id="element-1466" data-mp="1sn2y34r7p67d">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1467" data-mp="1dyf7ze04xoot" />
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-10 md:p-12" data-id="element-1468" data-mp="1iqq9glaixijh">
                <div className="shrink-0 text-center" data-id="element-1469" data-mp="19wl99b9i2ura">
                  <div className="text-6xl md:text-7xl font-extrabold font-heading text-brand-orange leading-none" data-id="element-1470" data-mp="19x3e2wa8ahat">
                    100%
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-3 text-lavande font-bold uppercase tracking-wider text-sm" data-id="element-1471" data-mp="zyxckfsc3d6h">
                    <Award className="w-5 h-5" data-id="element-1472" data-mp="gvbzcjf148s3" />
                    de réussite
                  </div>
                </div>
                <div className="w-px h-24 bg-white/10 hidden md:block" data-id="element-1473" data-mp="c17ss19oe2rj" />
                <div className="text-center md:text-left" data-id="element-1474" data-mp="74hcbctyl55l">
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-3" data-id="element-1475" data-mp="25eikojfilcx">
                    Session d'examen 2025 : 100 % de réussite
                  </h2>
                  <p className="text-lavande text-lg leading-relaxed" data-id="element-1476" data-mp="utk59gu1mkfe">
                    L'intégralité de nos candidats au TP Conseiller de Vente a
                    obtenu sa certification lors de la session d'examen 2025. Un
                    résultat qui récompense l'accompagnement individualisé et la
                    pédagogie par l'action de Zillenium Alternance.
                  </p>
                </div>
              </div>
            </div>

            {/* Centre d'examen agréé */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden" data-id="element-1477" data-mp="1l3kla3v5jvf5">
              <div className="h-2 w-full bg-primary" data-id="element-1478" data-mp="1q2ngkwlbyn9x" />
              <div className="p-8 md:p-12" data-id="element-1479" data-mp="1q36wk7rf71lb">
                <div className="flex items-start gap-5 mb-6" data-id="element-1480" data-mp="1v1qhq5y6w9cm">
                  <div className="w-14 h-14 rounded-2xl bg-lavande flex items-center justify-center shrink-0" data-id="element-1481" data-mp="1v0fgodw4pfp7">
                    <ShieldCheck className="w-7 h-7 text-primary" data-id="element-1482" data-mp="1zujqr60oy3ui" />
                  </div>
                  <div data-id="element-1483" data-mp="24ra6fs8l2xfe">
                    <span className="inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-2" data-id="element-1484" data-mp="28t54mnv7wuoy">
                      Centre d'examen agréé
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1485" data-mp="2irum4fj7cd4o">
                      Zillenium, centre d'examen du titre professionnel
                    </h2>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-1486" data-mp="odjzmdq2xv22">
                  Conformément à l'article L. 335-6 du code de l'éducation, le
                  titre professionnel est une certification délivrée au nom du
                  ministre chargé de l'emploi qui atteste de compétences
                  directement opérationnelles en entreprise. La délivrance de
                  cette certification s'appuie sur le respect des modalités
                  d'examen fixées réglementairement. Dans ce cadre, Zillenium
                  veille à ce que le responsable de session ainsi que les
                  membres de jury habilités bénéficient d'une information
                  complète et actualisée des modalités d'examen décrites dans le
                  référentiel de certification.
                </p>

                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6" data-id="element-1487" data-mp="3spayh6ofop0k">
                  <h3 className="font-bold text-primary-dark mb-2" data-id="element-1488" data-mp="3sr5jekzpgh4n">
                    Décision de renouvellement d'agrément n° 0350 — 7 mai 2026
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-1489" data-mp="3v85ynejlr7q0">
                    Agrément initialement délivré le 13 mai 2025, renouvelé par
                    cette décision pour la période en cours.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-1490" data-mp="3qanvy5w6hoqg">
                    <div data-id="element-1491" data-mp="3qcqkelhon9t2">
                      <span className="text-muted-foreground block mb-1" data-id="element-1492" data-mp="hv46lhymoqgo">
                        Autorité
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1493" data-mp="msw0uimqwoo1">
                        Préfète de la région Bourgogne-Franche-Comté (DREETS)
                      </span>
                    </div>
                    <div data-id="element-1494" data-mp="d2t6of3w14o4">
                      <span className="text-muted-foreground block mb-1" data-id="element-1495" data-mp="d21ap36dp4xb">
                        Validité totale de l'agrément
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1496" data-mp="15kou8e5q8bwa">
                        Du 13 mai 2025 au 12 mai 2027
                      </span>
                    </div>
                    <div data-id="element-1497" data-mp="2b2s8vuvl5vee">
                      <span className="text-muted-foreground block mb-1" data-id="element-1498" data-mp="2b1z3oyc9zdyd">
                        Lieu des sessions d'examen
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1499" data-mp="2ksu0gwn5dvy2">
                        25 rue Gabriel Cortel, 89300 Joigny
                      </span>
                    </div>
                    <div data-id="element-1500" data-mp="1o4nsbzr0olf1">
                      <span className="text-muted-foreground block mb-1" data-id="element-1501" data-mp="1s3wherb43v49">
                        Capacité par session
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1502" data-mp="1x1oavprenpak">
                        Jusqu'à 30 candidats
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>}

      {/* Dans la presse — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-white" data-id="element-1503" data-mp="11tmcyl4lwl0x">
          <div className="container max-w-4xl mx-auto" data-id="element-1504" data-mp="wv2s6o1r1fa5">
            <div className="text-center mb-10" data-id="element-1505" data-mp="wvkthz39qjox">
              <div className="inline-flex items-center justify-center p-3 bg-lavande rounded-2xl mb-4" data-id="element-1506" data-mp="s42tpeczep5j">
                <Newspaper className="w-7 h-7 text-primary" data-id="element-1507" data-mp="n5sw6x3sbcgr" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1508" data-mp="j3o6g9ggizze">
                Dans la presse
              </h2>
            </div>
            <a href="https://www.lyonne.fr/joigny-89300/actualites/il-y-a-un-manque-sur-joigny-des-etudiants-mobilises-pour-sensibiliser-au-don-du-sang-dans-la-galerie-marchande_14961315/" target="_blank" rel="noopener noreferrer" className="group block bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-1509" data-mp="e39uwhfnd7wm">
              <div className="flex flex-col sm:flex-row" data-id="element-1510" data-mp="1mh4xhmz0kcg">
                <div className="sm:w-48 shrink-0 bg-primary-dark flex flex-col items-center justify-center p-8 text-center" data-id="element-1511" data-mp="1o1zv8d765au">
                  <Newspaper className="w-10 h-10 text-lavande mb-3" data-id="element-1512" data-mp="3o1abn8kgqirs" />
                  <span className="text-white font-bold font-heading leading-tight" data-id="element-1513" data-mp="3nzh29ligtkg3">
                    L'Yonne Républicaine
                  </span>
                  <span className="text-lavande text-sm mt-1" data-id="element-1514" data-mp="kjs6x9zzlrvl">lyonne.fr</span>
                </div>
                <div className="p-8 flex-1" data-id="element-1515" data-mp="6k0lio75597k">
                  <span className="inline-block bg-brand-orange/10 text-brand-orange text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" data-id="element-1516" data-mp="6n4x46mxr789">
                    Engagement étudiant • Joigny
                  </span>
                  <h3 className="text-xl font-extrabold font-heading text-primary-dark mb-3 leading-snug group-hover:text-primary transition-colors" data-id="element-1517" data-mp="2vde7jiskdokz">
                    « Il y a un manque sur Joigny » : des étudiants mobilisés
                    pour sensibiliser au don du sang dans la galerie marchande
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4" data-id="element-1518" data-mp="2xq8b7qgg78xn">
                    Nos alternants s'engagent sur le terrain au service d'une
                    cause solidaire — une illustration concrète de la pédagogie
                    par l'action chez Zillenium Alternance.
                  </p>
                  <span className="inline-flex items-center text-primary font-bold group-hover:gap-2 transition-all" data-id="element-1519" data-mp="205c7o8m7ga8p">
                    Lire l'article
                    <ExternalLink className="w-4 h-4 ml-1" data-id="element-1520" data-mp="2a1f9fdd5a6v4" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>}

      {/* Témoignages d'alternants — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <>
          <div className="mt-24" data-id="element-1521" data-mp="kmphi0tk6wkc">
            <SocialProof title="On les accompagne dans leurs projets de marque" subtitle="Au-delà du diplôme, nos conseillers de vente créent, lancent et défendent de vrais projets de création de marque." testimonials={[{
          url: 'https://www.instagram.com/reel/DXKberwjoL6/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }, {
          url: 'https://www.instagram.com/p/DXNAq0yjk7y/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }]} featuredIndex={0} data-id="element-1522" data-mp="knr492jxt7hw" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1523" data-mp="3e6julibq6w2l">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1524" data-mp="3j3s998xo0hz3" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1525" data-mp="36siyditfxibc">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1526" data-mp="31v9860y892mg">
                Envie de vivre l'aventure ?
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1527" data-mp="3uivf9j6nype2">
                Rejoignez le TP Conseiller de Vente et donnez vie à vos projets
                commerciaux dès la rentrée.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1528" data-mp="3kjnwuhl2o4k3">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1529" data-mp="3gi2g2l5f364m">
                  <Link to="/candidater" data-id="element-1530" data-mp="252dv1zbq4h6g">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1531" data-mp="26mfffnpno4nl" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1532" data-mp="1wn6pd5xwva6i">
                  <Link to="/entreprises" data-id="element-1533" data-mp="1nsrzyajo2j9o">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}

      {/* Témoignages d'alternants — BTS NDRC uniquement */}
      {formation.id === 'bts-ndrc' && <>
          <div className="mt-24" data-id="element-1534" data-mp="1ivt6zvbqoid2">
            <SocialProof data-id="element-1535" data-mp="14w0lqfizeuch" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1536" data-mp="14y4ouv923pbp">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1537" data-mp="18zgircyyqwpp" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1538" data-mp="lbjnazpo3use">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1539" data-mp="zaj79u28jli8">
                Convaincu(e) ? Rejoignez la prochaine promotion
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1540" data-mp="1974hqux0xotg">
                Lancez votre candidature dès aujourd'hui et construisez votre
                avenir dans la relation client.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1541" data-mp="1hzg7eekbd88d">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1542" data-mp="1dzfpgjy0aewk">
                  <Link to="/candidater" data-id="element-1543" data-mp="1mxhowv23czhq">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1544" data-mp="2udujmtxpic7t" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1545" data-mp="33dhm7j269o2g">
                  <Link to="/entreprises" data-id="element-1546" data-mp="2fmc756422zt7">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}
    </div>;
}